import { AlertTriangle, Bell, BookOpen, Info, Megaphone, Pin, Shield } from 'lucide-react';

import {
  orderedPosts,
  postDate,
  type BoardCategoryIcon,
  type SafetyBoardPost,
  type SafetyBoardSettings,
} from './model';

const categoryIcon = {
  megaphone: Megaphone,
  shield: Shield,
  alert: AlertTriangle,
  book: BookOpen,
  bell: Bell,
  info: Info,
} satisfies Record<BoardCategoryIcon, typeof Megaphone>;

export function SafetyBoardCategoryChip({
  post,
  categories,
}: {
  post: SafetyBoardPost;
  categories: SafetyBoardSettings['categories'];
}) {
  const category = categories.find((item) => item.id === post.category);
  const Icon = categoryIcon[category?.icon ?? 'megaphone'];
  return (
    <span className="safety-board-category-chip" data-color={category?.color ?? 'navy'}>
      <Icon size={11} aria-hidden="true" />
      {category?.label ?? post.categoryLabel}
    </span>
  );
}

/**
 * The board row, shared by the work home block and the full board.
 *
 * Each compact archive row carries the configured category chip, title and date. Pinned posts
 * keep a fixed marker slot and unread state changes only from the server receipt.
 */
export function SafetyBoardRows({
  items,
  categories = [],
  onOpen,
}: {
  items: readonly SafetyBoardPost[];
  categories?: SafetyBoardSettings['categories'];
  onOpen: (post: SafetyBoardPost) => void;
}) {
  return (
    <div className="safety-board-rows">
      {orderedPosts(items).map((post) => {
        return (
          <button
            key={post.boardId}
            type="button"
            className="safety-board-row"
            data-board-id={post.boardId}
            data-read={String(post.read)}
            onClick={() => onOpen(post)}
          >
            <span className="safety-board-row-pin">
              {post.pinned ? <Pin size={12} aria-label="고정" /> : null}
            </span>
            <SafetyBoardCategoryChip post={post} categories={categories} />
            <span className="safety-board-row-title" data-title-color={post.titleColor}>
              {post.title}
            </span>
            <span className="safety-board-row-date">{postDate(post)}</span>
          </button>
        );
      })}
    </div>
  );
}
